import { createAsyncThunk } from "@reduxjs/toolkit"
import apiCall from "../../backend/http"
import { logout } from "../../slices/user_management_slice"

const userAuthenticationAction = createAsyncThunk(
  "auth/login",
  async (requestData, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post("/api/auth/login/", requestData)

      if (!data?.tokens?.access || !data?.tokens?.refresh || !data?.user) {
        throw new Error("Invalid response structure")
      }

      const { tokens, user } = data

      if (user.user_type === "CORPORATE") {
        return rejectWithValue({
          message:
            "Corporate users must use Corporate Login. Please click on 'Corporate Accounts' to login with your corporate credentials.",
          code: "CORPORATE"
        })
      }

      if (!user.is_verified) {
        return rejectWithValue({
          message:
            "Your account is not yet verified. Please contact an administrator.",
          code: "UNVERIFIED_USER"
        })
      }

      localStorage.clear()

      localStorage.setItem("accessToken", tokens.access)
      localStorage.setItem("refreshToken", tokens.refresh)

      return {
        user,
        tokens: {
          access: tokens.access,
          refresh: tokens.refresh
        }
      }
    } catch (error) {
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")

      if (
        error?.response?.status === 404 ||
        error?.response?.status === 403 ||
        error?.response?.data?.message?.toLowerCase().includes("verify") ||
        error?.response?.data?.message?.toLowerCase().includes("unverified")
      ) {
        return rejectWithValue({
          message:
            "Your account is not yet verified. Please contact an administrator.",
          code: "UNVERIFIED_USER"
        })
      }

      return rejectWithValue({
        message:
          error?.response?.data?.message || error?.message || "Login failed",
        code: error?.response?.data?.code || "LOGIN_FAILED"
      })
    }
  }
)

export const refreshTokenAction = createAsyncThunk(
  "auth/refresh",
  async (_, { getState, dispatch }) => {
    try {
      const refreshToken = localStorage.getItem("refreshToken")

      if (!refreshToken) {
        throw new Error("No refresh token available")
      }

      const { data } = await apiCall.post("/api/auth/refresh/", {
        refresh: refreshToken
      })

      localStorage.setItem("accessToken", data.access)
      dispatch(updateAuthHeader(data.access))

      return data.access
    } catch (error) {
      dispatch(logoutAction())
      throw error
    }
  }
)

export const updateAuthHeader = createAsyncThunk(
  "auth/updateHeader",
  async (accessToken) => {
    if (accessToken) {
      apiCall.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`
    } else {
      delete apiCall.defaults.headers.common["Authorization"]
    }
    return accessToken
  }
)

export const logoutAction = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("user")

    dispatch(logout())

    dispatch({ type: "LOGOUT" })
  }
)

const createUser = createAsyncThunk(
  "auth/createUser",
  async (postData, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post("/api/auth/register/", postData)

      localStorage.setItem("token", data?.data?.token)

      return data
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : { message: "Network error" }
      )
    }
  }
)

export const fetchAllSystemUsers = createAsyncThunk(
  "/auth/allSystemUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/users/list")

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message: error.response?.data?.message || "Failed to Retrieve users"
      })
    }
  }
)

export const updateUserVerification = createAsyncThunk(
  "auth/updateVerification",
  async ({ userId, isVerified }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/verify/`,
        {
          is_verified: isVerified
        }
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          "Failed to update verification status"
      })
    }
  }
)

export const corporateVerification = createAsyncThunk(
  "auth/corporateVerification",
  async ({ userId, isVerified, corporateProfileId }, { rejectWithValue }) => {
    try {
      if (!corporateProfileId || !userId) {
        return rejectWithValue({
          message: "Missing corporate profile ID or user ID"
        })
      }

      const response = await apiCall.post(
        `/api/corporate-profiles/${corporateProfileId}/verify-user/${userId}/`,
        { is_verified: isVerified }
      )

      return {
        data: response.data.user || { id: userId, is_verified: isVerified },
        message:
          response.data.message || "Verification status updated successfully"
      }
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          "Failed to update corporate user verification status"
      })
    }
  }
)

export const corporateUnverifyUser = createAsyncThunk(
  "auth/corporateUnverifyUser",
  async ({ userId, corporateProfileId }, { rejectWithValue }) => {
    try {
      if (!corporateProfileId || !userId) {
        return rejectWithValue({
          message: "Missing corporate profile ID or user ID"
        })
      }

      const response = await apiCall.post(
        `/api/corporate-profiles/${corporateProfileId}/unverify-user/${userId}/`,
        { is_verified: false }
      )

      return {
        data: response.data.user || { id: userId, is_verified: false },
        message: response.data.message || "User unverified successfully"
      }
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message || "Failed to unverify corporate user"
      })
    }
  }
)

export const createCorporateUser = createAsyncThunk(
  "auth/createCorporateUser",
  async (postData, { rejectWithValue }) => {
    try {
      const corporate_profile = postData.corporate_profile

      const response = await apiCall.post(
        `/api/corporate/${corporate_profile}/create_user/`,
        postData,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to Retrieve users"
      })
    }
  }
)

export const updateCorporateUserProfile = createAsyncThunk(
  "auth/updateCorporateUserProfile",
  async ({ profileData, files }, { rejectWithValue, getState }) => {
    try {
      const formData = new FormData()

      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value)
        }
      })

      if (files?.vatCertificate) {
        formData.append("vat_certificate", files.vatCertificate)
      }

      if (files?.incorporationDocument) {
        formData.append("incorporation_document", files.incorporationDocument)
      }

      // Get the corporate profile ID
      const state = getState()
      const corporateId =
        profileData.corporate_profile_id ||
        state.userMngmt?.user?.corporate_profile ||
        (typeof state.userMngmt?.user?.corporate_profile === "object"
          ? state.userMngmt?.user?.corporate_profile.id
          : null)

      if (!corporateId) {
        throw new Error("No corporate profile ID found for update operation")
      }

      const response = await apiCall.put(
        `/api/corporate-profiles/${corporateId}/update_profile/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update corporate user profile",
        error: error.response?.data || error.message
      })
    }
  }
)

export const fetchCorporateProfiles = createAsyncThunk(
  "auth/fetchCorporateProfiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/corporate-profiles/")

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message:
          error.response?.data?.message ||
          "Failed to retrieve corporate profiles"
      })
    }
  }
)

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async ({ userId, profileData }, { rejectWithValue }) => {
    try {
      const response = await apiCall.put(
        `/api/user-verification/${userId}/update-profile/`,
        profileData
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message || "Failed to update user profile"
      })
    }
  }
)

export const changeUserPassword = createAsyncThunk(
  "auth/changeUserPassword",
  async ({ userId, passwordData }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/change-password/`,
        passwordData
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Failed to change password"
      })
    }
  }
)

export const unverifyUser = createAsyncThunk(
  "auth/unverifyUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/unverify/`
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Failed to unverify user"
      })
    }
  }
)

export const resetUserPassword = createAsyncThunk(
  "auth/resetUserPassword",
  async ({ userId, passwordData }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/admin-reset-password/`,
        passwordData
      )

      return response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message || "Failed to reset user password"
      })
    }
  }
)

export const corporateLogin = createAsyncThunk(
  "auth/corporateLogin",
  async (requestData, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post("/api/corporate/login/", requestData)

      if (!data?.tokens?.access || !data?.tokens?.refresh || !data?.user) {
        throw new Error("Invalid response structure")
      }

      const { tokens, user } = data

      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("user")

      localStorage.setItem("accessToken", tokens.access)
      localStorage.setItem("refreshToken", tokens.refresh)
      localStorage.setItem("user", JSON.stringify(user))

      return {
        user,
        tokens: {
          access: tokens.access,
          refresh: tokens.refresh
        }
      }
    } catch (error) {
      if (
        error?.response?.status === 404 ||
        error?.response?.status === 403 ||
        error?.response?.data?.message?.toLowerCase().includes("verify") ||
        error?.response?.data?.message?.toLowerCase().includes("unverified")
      ) {
        return rejectWithValue({
          message:
            "Your corporate account is not yet verified. Please contact an administrator.",
          code: "UNVERIFIED_USER"
        })
      }

      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Corporate login failed",
        code: error?.response?.data?.code || "LOGIN_FAILED"
      })
    }
  }
)

export const fetchCorporateUsers = createAsyncThunk(
  "/auth/corporateUsers",
  async (corporateProfileId, { rejectWithValue }) => {
    try {
      if (!corporateProfileId) {
        throw new Error("Corporate profile ID is required")
      }

      const response = await apiCall.get(
        `/api/corporate-profiles/${corporateProfileId}/list_users/`
      )

      if (!response.data) {
        throw new Error("No data received")
      }

      return response.data.users || []
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message: error.response?.data?.message
      })
    }
  }
)

export const createCorporateProfile = createAsyncThunk(
  "auth/createCorporateProfile",
  async ({ profileData, files }, { rejectWithValue }) => {
    try {
      const formData = new FormData()

      formData.append("company_name", profileData.company_name)
      formData.append("tax_id", profileData.tax_id)
      formData.append("address", profileData.address)

      formData.append("registration_number", profileData.registration_number)
      formData.append("industry_type", profileData.industry_type)

      // Add optional fields with defaults if not provided
      formData.append(
        "billing_address",
        profileData.billing_address || profileData.address
      )
      formData.append(
        "billing_email",
        profileData.billing_email || "user@example.com"
      )
      formData.append("billing_phone", profileData.billing_phone || "")
      formData.append("website", profileData.website || "")

      // Try setting these to 0 and 30 directly instead of strings
      formData.append("credit_limit", 0)
      formData.append("payment_terms", "30")

      // Add contract dates using current date if not provided
      const today = new Date()
      const oneYearLater = new Date(today)
      oneYearLater.setFullYear(today.getFullYear() + 1)

      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }

      formData.append(
        "contract_start_date",
        profileData.contract_start_date || formatDate(today)
      )
      formData.append(
        "contract_end_date",
        profileData.contract_end_date || formatDate(oneYearLater)
      )

      // Add position if provided
      if (profileData.position) {
        formData.append("position", profileData.position)
      }

      // Add files if provided
      if (files?.vatCertificate) {
        formData.append("vat_certificate", files.vatCertificate)
      }

      if (files?.incorporationDocument) {
        formData.append("incorporation_document", files.incorporationDocument)
      }

      // Try with modified headers to ensure proper handling
      const response = await apiCall.post(
        "/api/corporate-profiles/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json"
          },
          // Add timeout to ensure we don't wait too long
          timeout: 30000
        }
      )

      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to create corporate profile"
        }
      )
    }
  }
)

export const updateCorporateProfile = createAsyncThunk(
  "auth/updateCorporateProfile",
  async ({ corporateId, profileData, files }, { rejectWithValue }) => {
    try {
      if (!corporateId) {
        throw new Error("Corporate profile ID is required")
      }

      // Create FormData to handle file uploads properly
      const formData = new FormData()

      // Add basic profile data
      if (profileData.company_name)
        formData.append("company_name", profileData.company_name)
      if (profileData.tax_id) formData.append("tax_id", profileData.tax_id)
      if (profileData.address) formData.append("address", profileData.address)
      if (profileData.registration_number)
        formData.append("registration_number", profileData.registration_number)
      if (profileData.industry_type)
        formData.append("industry_type", profileData.industry_type)

      // Add optional fields
      if (profileData.billing_address)
        formData.append("billing_address", profileData.billing_address)
      if (profileData.billing_email)
        formData.append("billing_email", profileData.billing_email)
      if (profileData.billing_phone)
        formData.append("billing_phone", profileData.billing_phone)
      if (profileData.website) formData.append("website", profileData.website)
      if (profileData.credit_limit)
        formData.append("credit_limit", profileData.credit_limit)
      if (profileData.payment_terms)
        formData.append("payment_terms", profileData.payment_terms)
      if (profileData.position)
        formData.append("position", profileData.position)

      // Contract dates
      if (profileData.contract_start_date)
        formData.append("contract_start_date", profileData.contract_start_date)
      if (profileData.contract_end_date)
        formData.append("contract_end_date", profileData.contract_end_date)

      // Add files if provided
      if (files?.vatCertificate) {
        formData.append("vat_certificate", files.vatCertificate)
      }

      if (files?.incorporationDocument) {
        formData.append("incorporation_document", files.incorporationDocument)
      }

      // Use the correct endpoint for updating corporate profile
      const response = await apiCall.put(
        `/api/corporate-profiles/${corporateId}/update_profile/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data" // Important for file uploads
          }
        }
      )

      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to update corporate profile"
        }
      )
    }
  }
)

// Fetch a corporate profile by ID
export const fetchCorporateProfileById = createAsyncThunk(
  "corporate/fetchProfileById",
  async (profileId, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(
        `/api/corporate-profiles/${profileId}/`
      )

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Failed to fetch corporate profile"
      })
    }
  }
)

export const blockUser = createAsyncThunk(
  "auth/blockUser",
  async ({ userId, reason }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/block/`,
        { reason }
      )
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Failed to block user"
      })
    }
  }
)

// Unblock a user
export const unblockUser = createAsyncThunk(
  "auth/unblockUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/unblock/`
      )
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Failed to unblock user"
      })
    }
  }
)

// List blocked users
export const fetchBlockedUsers = createAsyncThunk(
  "auth/fetchBlockedUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/user-verification/blocked/")

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Failed to fetch blocked users"
      })
    }
  }
)

// Send reminder to a user (admin)
export const sendUserReminder = createAsyncThunk(
  "auth/sendUserReminder",
  async ({ userId, subject, message }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/user-verification/${userId}/send-reminder/`,
        { subject, message }
      )
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Failed to send reminder"
      })
    }
  }
)

// Send corporate-scoped reminder
export const sendCorporateReminder = createAsyncThunk(
  "auth/sendCorporateReminder",
  async (
    { corporateProfileId, corporateUserId, subject, message },
    thunkAPI
  ) => {
    try {
      const response = await apiCall.post(
        `/api/corporate/${corporateProfileId}/send-reminder/${corporateUserId}/`,
        { subject, message }
      )
      return response.data
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: "Failed to send corporate reminder"
      })
    }
  }
)

// Filter users by block status
export const fetchUsersByBlockStatus = createAsyncThunk(
  "auth/fetchUsersByBlockStatus",
  async (isBlocked = true, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(
        `/api/user-verification/?is_blocked=${isBlocked}`
      )

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          "Failed to fetch users by block status"
      })
    }
  }
)

export const LOGOUT = "LOGOUT"

export { createUser, userAuthenticationAction }
